import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, DataTable } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { usePermissions } from '../../hooks/usePermission';
import { getRelativeTime } from '../../utils/formatRelativeTime';
import { formatCell } from '../../utils/formatCell';
import { isPlaceholderProfile } from '../../utils/memberDisplay';
import UserTypeBadge from '../shared/UserTypeBadge';
import MemberNameCell from './MemberNameCell';
import MemberRowActions from './MemberRowActions';
import MembersAddedCell from './MembersAddedCell';

/**
 * Members table (list-page results band): `DataTable` with the prototype columns. Clicking a row
 * opens the member (relative link, embedded-safe).
 *
 * @param {object} props
 * @param {object[]} props.rows UI members (`mapApiMemberToUi`).
 * @param {boolean} [props.isLoading]
 * @param {object} [props.selection] Return value of `useMemberSelection`: adds the checkbox column.
 * @param {(actionId: string, member: object) => void} [props.onRowAction] Adds the kebab column.
 * @param {string} [props.newBatchId] Import batch auto-applied on arrival: its members get the New badge.
 */
function MembersTable({ rows, isLoading = false, newBatchId = '', selection, onRowAction }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { actor, mode } = usePermissions();

  const countLabel = useCallback(
    (key, items) => formatCell(items?.length ? t(key, { count: items.length }) : null),
    [t],
  );

  const columns = useMemo(
    () => [
      {
        key: 'displayName',
        label: t('members.columns.member'),
        render: (_, member) => (
          <MemberNameCell member={member} isNew={Boolean(newBatchId) && member.importBatchId === newBatchId} />
        ),
      },
      {
        key: 'userType',
        label: t('members.columns.userType'),
        render: (userType) => <UserTypeBadge userType={userType} />,
      },
      {
        key: 'roles',
        label: t('members.columns.roles'),
        render: (roles) => countLabel('members.count.roles', roles),
      },
      {
        key: 'groups',
        label: t('members.columns.groups'),
        render: (groups) => countLabel('members.count.groups', groups),
      },
      {
        key: 'mfaEnabled',
        label: t('members.columns.mfa'),
        render: (enabled, member) =>
          isPlaceholderProfile(member) ? (
            formatCell(null)
          ) : (
            <Badge variant={enabled ? 'success' : 'warning'}>
              {t(enabled ? 'members.mfa.on' : 'members.mfa.off')}
            </Badge>
          ),
      },
      {
        key: 'lastActiveAt',
        label: t('members.columns.lastActive'),
        render: (lastActiveAt, member) => {
          const relative = isPlaceholderProfile(member) ? null : getRelativeTime(lastActiveAt);
          return formatCell(
            relative ? t(`members.relative.${relative.key}`, { count: relative.count }) : null,
          );
        },
      },
      {
        key: 'addedAt',
        label: t('members.columns.added'),
        render: (addedAt) => <MembersAddedCell addedAt={addedAt} />,
      },
      ...(onRowAction
        ? [
            {
              key: 'actions',
              label: <span className="sr-only">{t('members.columns.actions')}</span>,
              cellClassName: 'w-12',
              render: (_, member) => (
                <MemberRowActions member={member} actor={actor} mode={mode} onAction={onRowAction} />
              ),
            },
          ]
        : []),
    ],
    [t, countLabel, newBatchId, onRowAction, actor, mode],
  );

  const tableSelection = useMemo(
    () =>
      selection && {
        enabled: true,
        isRowSelected: selection.isRowSelected,
        onToggleRow: selection.onToggleRow,
        onToggleAll: selection.onToggleAll,
        allSelected: selection.allSelected,
        selectAllDisabled: rows.length === 0,
        selectAllAriaLabel: t('members.bulk.selectAll'),
        rowSelectAriaLabel: (member) => t('members.bulk.selectRow', { name: member.displayName }),
      },
    [selection, rows.length, t],
  );

  const handleRowClick = useCallback((member) => navigate(member.id), [navigate]);

  return (
    // Below `lg` the 880px table scrolls inside its own box instead of widening the whole page; from
    // `lg` it fits, and staying `visible` keeps the sticky header attached to the page scroller.
    <div className="overflow-x-auto lg:overflow-visible">
      <DataTable
        columns={columns}
        data={rows}
        loading={isLoading}
        minTableWidth={880}
        selection={tableSelection}
        onRowClick={handleRowClick}
      />
    </div>
  );
}

export default memo(MembersTable);
